import React, { useState, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axiosInstance from '../../config/axiosInstance';
import { StationContext } from '../context/StationContext';


const REPORTS = [
  {
    id: 'employee_activity',
    name: 'Actividad de personal',
    description: 'Horas trabajadas, licencias y actividad por persona, filtrado por año, mes y estación.',
    endpoint: '/reports/employee_activity',
    filters: ['year', 'month'],
    responseType: 'employees',
    columns: [
      { field: 'nombre', label: 'Nombre' },
      { field: 'ci', label: 'Cédula' },
      { field: 'posicion', label: 'Área' },
      { field: 'trabajados', label: 'Días Trabajados' },
      { field: 'libres', label: 'Días Libres' },
      { field: 'falta', label: 'Faltas' },
      { field: 'licencia', label: 'Licencia' },
      { field: 'horasDiurnas', label: 'Horas Diurnas' },
      { field: 'horasNocturnas', label: 'Horas Nocturnas' },
      { field: 'totalHoras', label: 'Total Horas' },
      { field: 'horasPorDia', label: 'Horas/Día' },
      { field: 'horasExtra', label: 'Horas Extra' },
      { field: 'horasRecuperacion', label: 'Horas Recuperación' },
    ],
  },
  {
    id: 'public_holidays',
    name: 'Feriados',
    description: 'Balance anual de feriados trabajados y FER por persona, tomando la tabla oficial y la asignación manual/automática.',
    endpoint: '/reports/public_holidays',
    filters: ['year'],
    responseType: 'rows',
    columns: [
      { field: 'nombre', label: 'Nombre' },
      { field: 'ci', label: 'Cédula' },
      { field: 'posicion', label: 'Área' },
      { field: 'fecha_ingreso', label: 'Fecha ingreso' },
      { field: 'feriados_configurados', label: 'Feriados configurados' },
      { field: 'feriados_ocurridos', label: 'Feriados ocurridos' },
      { field: 'feriados_trabajados', label: 'Trabajó' },
      { field: 'feriados_fer', label: 'FER' },
      { field: 'sin_registro', label: 'Sin registro' },
      { field: 'meta_fer_anual', label: 'Meta FER anual' },
      { field: 'diferencia_meta', label: 'Diferencia meta' },
      { field: 'ultimo_feriado', label: 'Último feriado' },
      { field: 'ultimo_estado', label: 'Último estado' },
    ],
  },
  {
    id: 'employee_directory',
    name: 'Datos de empleados',
    description: 'Ficha de datos maestros por empleado: cédula, estación, área, posición y otros datos relevantes.',
    endpoint: '/reports/employee_directory',
    filters: [],
    responseType: 'rows',
    columns: [
      { field: 'ci', label: 'Cédula' },
      { field: 'estacion', label: 'Estación' },
      { field: 'nombre', label: 'Nombre' },
      { field: 'apellido', label: 'Apellido' },
      { field: 'fecha_ingreso', label: 'Fecha de Ingreso' },
      { field: 'posicion', label: 'Área' },
      { field: 'workarea', label: 'Posición' },
      { field: 'area_auxiliar', label: 'Área auxiliar' },
      { field: 'nocturnidad', label: 'Nocturnidad' },
      { field: 'encargado', label: 'Encargado' },
      { field: 'estado', label: 'Estado' },
    ],
  },
];

const currentYear = new Date().getFullYear();

function ReportsPage() {
  const { selectedStation } = useContext(StationContext);
  const [searchParams] = useSearchParams();

  const qpStation = searchParams.get('station');
  const qpPosition = searchParams.get('position');
  const qpStartDate = searchParams.get('start_date');
  const qpEndDate = searchParams.get('end_date');
  const qpReport = searchParams.get('report');

  const [selectedReportId, setSelectedReportId] = useState(
    qpReport && REPORTS.some((report) => report.id === qpReport) ? qpReport : 'employee_activity'
  );
  const [filters, setFilters] = useState({
    year: currentYear,
    month: new Date().getMonth() + 1,
  });
  const [periodContext, setPeriodContext] = useState({
    station: qpStation || '',
    position: qpPosition || '',
    start_date: qpStartDate || '',
    end_date: qpEndDate || '',
  });
  const [showPeriodContext, setShowPeriodContext] = useState(
    Boolean(qpStartDate || qpEndDate || qpStation || qpPosition)
  );
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportMeta, setReportMeta] = useState(null);
  const [errorInfo, setErrorInfo] = useState({ message: '', severity: 'error' });
  const [localFilters, setLocalFilters] = useState({
    search: '',
    position: 'ALL',
  });
  const [includeInactive, setIncludeInactive] = useState(false);

  const selectedReport = REPORTS.find((report) => report.id === selectedReportId);
  const prefillFromUrlRef = useRef(false);
  const autoFetchedRef = useRef(false);

  useEffect(() => {
    const hasAnyQP = Boolean(qpStartDate || qpEndDate || qpStation || qpPosition);
    setPeriodContext({
      station: qpStation || '',
      position: qpPosition || '',
      start_date: qpStartDate || '',
      end_date: qpEndDate || '',
    });
    setShowPeriodContext(hasAnyQP);
    autoFetchedRef.current = false;
  }, [qpStation, qpPosition, qpStartDate, qpEndDate]);

  useEffect(() => {
    if (!qpStartDate) return;
    const dateValue = new Date(`${qpStartDate}T00:00:00`);
    if (Number.isNaN(+dateValue)) return;

    prefillFromUrlRef.current = true;
    setFilters((prev) => ({
      ...prev,
      year: dateValue.getFullYear(),
      month: dateValue.getMonth() + 1,
    }));
    queueMicrotask(() => {
      prefillFromUrlRef.current = false;
    });
  }, [qpStartDate]);

  const transformEmployeeData = (employees) => {
    if (!Array.isArray(employees) || employees.length === 0) return [];

    return employees
      .map((emp) => {
        const horasDiurnas = emp.horas?.diurnas_min || 0;
        const horasNocturnas = emp.horas?.nocturnas_min || 0;
        const horasExtraMin = emp.horas?.extra_min || 0;
        const horasRecuperacionMin = emp.horas?.recuperacion_min || 0;
        const totalMinutos = horasDiurnas + horasNocturnas;
        const totalHoras = (totalMinutos / 60).toFixed(2);
        const diasTrabajados = emp.dias?.trabajados || 0;

        return {
          nombre: emp.nombre || 'N/A',
          ci: emp.ci || 'N/A',
          posicion: emp.posiciones?.[0] || '-',
          areas: emp.areas?.join(', ') || '-',
          trabajados: diasTrabajados,
          libres: emp.dias?.libres || 0,
          falta: emp.dias?.falta || 0,
          licencia: emp.dias?.licencia || 0,
          horasDiurnas: (horasDiurnas / 60).toFixed(2),
          horasNocturnas: (horasNocturnas / 60).toFixed(2),
          totalHoras,
          horasPorDia: diasTrabajados > 0 ? (Number(totalHoras) / diasTrabajados).toFixed(2) : '0.00',
          horasExtra: (horasExtraMin / 60).toFixed(2),
          horasRecuperacion: (horasRecuperacionMin / 60).toFixed(2),
        };
      })
      .filter(Boolean);
  };

  const normalizeIncludedPeriods = (meta) => {
    const list = Array.isArray(meta?.included_periods) ? meta.included_periods : [];
    const seen = new Set();
    const out = [];

    for (const period of list) {
      const key = `${period?.station_id ?? ''}|${period?.position_id ?? ''}|${period?.start_date ?? ''}|${period?.end_date ?? ''}|${period?.status ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(period);
    }

    out.sort((a, b) => {
      const positionA = Number(a?.position_id ?? 0);
      const positionB = Number(b?.position_id ?? 0);
      if (positionA !== positionB) return positionA - positionB;
      return String(a?.start_date ?? '').localeCompare(String(b?.start_date ?? ''));
    });

    return out;
  };

  const expectedPeriodsCount = (meta) => {
    const expected = Array.isArray(meta?.expected_periods) ? meta.expected_periods : [];
    return expected.length || 0;
  };

  const buildParams = useCallback(() => {
    const params = {
      station_id: Number(selectedStation?.id || 0),
    };

    if (selectedReport?.filters.includes('year')) {
      params.year = Number(filters.year);
    }

    if (selectedReport?.filters.includes('month')) {
      params.month = Number(filters.month);
    }

    if (selectedReportId === 'employee_directory') {
      params.active_only = !includeInactive;
    }

    return params;
  }, [filters.year, filters.month, selectedReport, selectedReportId, selectedStation?.id, includeInactive]);

  const handleFilterChange = (field) => (event) => {
    if (!prefillFromUrlRef.current) {
      setShowPeriodContext(false);
    }
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleLocalFilterChange = (field) => (event) => {
    setLocalFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleFetchData = useCallback(async () => {
    if (!selectedReport || !selectedStation?.id) {
      setErrorInfo({ severity: 'warning', message: 'Falta seleccionar una estación.' });
      setReportMeta(null);
      return;
    }

    setLoading(true);
    setErrorInfo({ message: '', severity: 'error' });

    try {
      const response = await axiosInstance.get(selectedReport.endpoint, {
        params: buildParams(),
      });

      setReportMeta(response.data?.meta || null);

      const reportRows = selectedReport.responseType === 'employees'
        ? transformEmployeeData(response.data?.employees)
        : Array.isArray(response.data?.rows)
          ? response.data.rows
          : Array.isArray(response.data)
            ? response.data
            : [];

      setRows(reportRows);
      setColumns(selectedReport.columns);
      setLocalFilters({ search: '', position: 'ALL' });

      if (reportRows.length === 0) {
        setErrorInfo({
          severity: 'info',
          message: 'No hay datos para esos filtros. Los reportes se generan únicamente con información ya disponible en el sistema.',
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const rawMsg =
        data?.message ||
        data?.error ||
        data?.detail ||
        (typeof data === 'string' ? data : '') ||
        err?.message ||
        'Ocurrió un error al obtener el reporte.';

      setRows([]);
      setColumns(selectedReport.columns);
      setReportMeta(null);

      if (status === 404) {
        setErrorInfo({
          severity: 'info',
          message: rawMsg,
        });
        return;
      }

      setErrorInfo({
        severity: status && status >= 500 ? 'error' : 'warning',
        message: rawMsg,
      });
    } finally {
      setLoading(false);
    }
  }, [buildParams, selectedReport, selectedStation?.id]);

  useEffect(() => {
    const hasPeriod = Boolean(periodContext?.start_date && periodContext?.end_date);
    if (!hasPeriod || !selectedStation?.id || !selectedReport || autoFetchedRef.current) return;

    autoFetchedRef.current = true;
    handleFetchData();
  }, [
    periodContext?.start_date,
    periodContext?.end_date,
    selectedStation?.id,
    selectedReport,
    handleFetchData,
  ]);

  const positions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.posicion))).filter(Boolean),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const text = localFilters.search.toLowerCase();

    return rows.filter((row) => {
      const matchesText =
        !text ||
        String(row.nombre || '').toLowerCase().includes(text) ||
        String(row.apellido || '').toLowerCase().includes(text) ||
        String(row.ci || '').includes(text);

      const matchesPosition =
        localFilters.position === 'ALL' || row.posicion === localFilters.position;

      return matchesText && matchesPosition;
    });
  }, [rows, localFilters]);

  const handleDownloadExcel = () => {
    if (!filteredRows.length) return;

    const exportData = filteredRows.map((row) => {
      const item = {};
      columns.forEach((column) => {
        item[column.label] = row[column.field];
      });
      return item;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    let suffix;
    if (selectedReport?.filters.includes('month')) {
      suffix = `${filters.year}_${String(filters.month).padStart(2, '0')}`;
    } else if (selectedReport?.filters.includes('year')) {
      suffix = `${filters.year}`;
    } else {
      suffix = new Date().toISOString().slice(0, 10);
    }

    saveAs(blob, `${selectedReportId}_${suffix}.xlsx`);
  };

  const stationMismatch =
    showPeriodContext &&
    Boolean(periodContext?.station) &&
    Boolean(selectedStation?.id) &&
    String(periodContext.station) !== String(selectedStation.id);

  const includedPeriods = useMemo(() => normalizeIncludedPeriods(reportMeta), [reportMeta]);
  const isPartialMonth = useMemo(() => {
    const expected = expectedPeriodsCount(reportMeta);
    const included = includedPeriods.length;
    return expected > 0 && included > 0 && included < expected;
  }, [reportMeta, includedPeriods.length]);
  const hasMetaBlock = includedPeriods.length > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Reportes
      </Typography>

      {showPeriodContext && (periodContext?.start_date || periodContext?.end_date) && (
        <Alert severity={stationMismatch ? 'warning' : 'info'} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Período seleccionado
          </Typography>
          <Typography variant="body2">
            {periodContext.start_date || '—'} → {periodContext.end_date || '—'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
            Estación (URL): <b>{periodContext.station || '—'}</b> · Área (URL): <b>{periodContext.position || '—'}</b>
            {selectedStation?.id ? <> · Estación activa (UI): <b>{selectedStation.id}</b></> : null}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Tipos de reporte
            </Typography>
            <List dense>
              {REPORTS.map((report) => (
                <ListItemButton
                  key={report.id}
                  selected={report.id === selectedReportId}
                  onClick={() => {
                    setSelectedReportId(report.id);
                    autoFetchedRef.current = false;
                    setRows([]);
                    setColumns([]);
                    setLocalFilters({ search: '', position: 'ALL' });
                    setErrorInfo({ message: '', severity: 'error' });
                    setReportMeta(null);
                    setIncludeInactive(false);
                  }}
                >
                  <AssessmentIcon fontSize="small" sx={{ mr: 1 }} />
                  <ListItemText primary={report.name} secondary={report.description} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {selectedReport?.name || 'Reporte'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedReport?.description || ''}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {selectedReport?.filters.includes('year') && (
                <Grid item xs={12} sm={selectedReport?.filters.includes('month') ? 6 : 12}>
                  <TextField
                    fullWidth
                    label="Año"
                    type="number"
                    value={filters.year}
                    onChange={handleFilterChange('year')}
                  />
                </Grid>
              )}

              {selectedReport?.filters.includes('month') && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Mes"
                    value={filters.month}
                    onChange={handleFilterChange('month')}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                      <MenuItem key={month} value={month}>
                        {String(month).padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {selectedReportId === 'employee_directory' && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeInactive}
                        onChange={(event) => setIncludeInactive(event.target.checked)}
                      />
                    }
                    label="Incluir empleados inactivos"
                  />
                </Grid>
              )}
            </Grid>

            {hasMetaBlock && (
              <Alert severity={isPartialMonth ? 'warning' : 'info'} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Períodos incluidos en el reporte
                </Typography>
                {includedPeriods.map((period, index) => (
                  <Typography key={`${period?.position_id}-${period?.start_date}-${index}`} variant="body2">
                    Área {period?.position_id ?? '—'}: {period?.start_date ?? '—'} → {period?.end_date ?? '—'}
                  </Typography>
                ))}
              </Alert>
            )}

            {rows.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={positions.length ? 6 : 12}>
                  <TextField
                    fullWidth
                    label="Buscar por nombre o cédula"
                    value={localFilters.search}
                    onChange={handleLocalFilterChange('search')}
                  />
                </Grid>
                {positions.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Área"
                      value={localFilters.position}
                      onChange={handleLocalFilterChange('position')}
                    >
                      <MenuItem value="ALL">Todas</MenuItem>
                      {positions.map((position) => (
                        <MenuItem key={position} value={position}>
                          {position}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
              </Grid>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button variant="contained" onClick={handleFetchData} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Ver datos'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
                disabled={filteredRows.length === 0}
              >
                Descargar Excel
              </Button>
            </Box>

            {errorInfo.message && (
              <Alert severity={errorInfo.severity} sx={{ mb: 2 }}>
                {errorInfo.message}
              </Alert>
            )}

            {filteredRows.length > 0 ? (
              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell key={column.field} sx={{ fontWeight: 'bold' }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row, index) => (
                      <TableRow key={index} hover>
                        {columns.map((column) => (
                          <TableCell key={column.field}>{row[column.field]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              !loading &&
              !errorInfo.message && (
                <Typography variant="body2" color="text.secondary">
                  No hay datos. Presiona “Ver datos”.
                </Typography>
              )
            )}

            {rows.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Total de empleados: {filteredRows.length} / {rows.length}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ReportsPage;
